import { createContext } from '@direct-frontend/modules-context';
import { LinkedList, createLinkedList } from '../utils';

/**
 * Ключ для кеширования
 */
type CacheKey = unknown[] | IArguments;

/**
 * Запись в кеше
 */
interface CacheEntry<T = unknown> {
    key: CacheKey;
    value: T;
}

/**
 * Кеш для использования в функции мемоизации
 */
interface Cache<T = unknown> {
    get(key: CacheKey): CacheEntry<T> | undefined;
    set(key: CacheKey, value: T): void;
}

export const { getContext: getSingletonCacheContext } = createContext({
    name: '@direct-frontend/modules-store/createSelector/singletonCacheContext',
    getInitialValue: () => new Map<Function, unknown>(),
});

/** Параметры кэша */
export interface CreateCacheOptions {
    /* Функция для сравнения каждого аргумента в ключе кэширования */
    compareArgFn: (prev: unknown, next: unknown) => boolean;
}

/**
 * Кеш, который хранит только одно значение
 * @returns Cache
 */
export function createSingletonCache<T>(
    func: Function,
    { compareArgFn }: CreateCacheOptions = { compareArgFn: Object.is }
): Cache<T> {
    return {
        get(key) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            const entry = getSingletonCacheContext().get(func) as CacheEntry<T> | undefined;
            if (entry && equalCacheKeys(entry.key, key, compareArgFn)) {
                return entry;
            }
            return;
        },

        set(key, value) {
            getSingletonCacheContext().set(func, { key, value });
        },
    };
}

export const { getContext: getLruCacheContext } = createContext<Map<Function, LinkedList<unknown>>>({
    name: '@direct-frontend/modules-store/createSelector/lruCacheContext',
    getInitialValue: () => new Map(),
});

/**
 * Кеш, который хранит N самых актуальных значений
 * @returns Cache
 */
export function createLruCache<T>(
    func: Function,
    maxSize: number,
    { compareArgFn }: CreateCacheOptions = { compareArgFn: Object.is }
): Cache<T> {
    function getEntries() {
        const context = getLruCacheContext();
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        let entries = context.get(func) as LinkedList<CacheEntry<T>> | undefined;
        if (!entries) {
            entries = createLinkedList();
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            context.set(func, entries as LinkedList<unknown>);
        }
        return entries;
    }

    return {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error DIRECT-235591 TS7030
        get(key) {
            const entries = getEntries();
            let item = entries.head();
            while (item) {
                if (equalCacheKeys(item.value.key, key, compareArgFn)) {
                    entries.setHead(item);
                    return item.value;
                }

                item = item.next;
            }
        },

        set(key, value) {
            const entries = getEntries();
            let item = entries.head();
            while (item) {
                if (equalCacheKeys(item.value.key, key, compareArgFn)) {
                    entries.remove(item);
                }

                item = item.next;
            }

            entries.prepend({ key, value });

            if (entries.size() > maxSize) {
                const tail = entries.tail();
                if (tail) {
                    entries.remove(tail);
                }
            }
        },
    };
}

/**
 * Функция для сравнения ключей кэширования (список аргументов вызова)
 * @param prev предыдущие аргументы
 * @param next новые аргументы
 * @param compareFn Функция для сравнения каждого элемента в ключе
 * @returns истина, если ключи равны
 */
function equalCacheKeys(prev: CacheKey | null, next: CacheKey | null, compareFn = Object.is): boolean {
    if (prev === null || next === null) {
        return false;
    }

    if (prev === next) {
        return true;
    }

    if (prev.length !== next.length) {
        return false;
    }

    const length = prev.length;
    for (let i = 0; i < length; i++) {
        if (!compareFn(prev[i], next[i])) {
            return false;
        }
    }

    return true;
}

/**
 * Очищает закешированные данные в селекторах
 */
export function cleanupCache() {
    getSingletonCacheContext().clear();
    getLruCacheContext().clear();
}
